import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../Assets/Logo/logo.png';
import {
  Building2, MapPin, Phone, Mail, ShieldCheck,
  Sparkles, CreditCard, ChevronDown, ChevronUp,
  ArrowRight, Globe, Award, Clock, Compass
} from 'lucide-react';

function Footer() {
  const navigate = useNavigate();
  const [mobileExpandedSection, setMobileExpandedSection] = useState(null);

  const [contactDetails, setContactDetails] = useState({
    address: '67 C21 Res-Com Mall, Nanakheda, Ujjain, Madhya Pradesh 456010, India',
    email: 'contact@zoyotrip.com',
    phone: '+91-8978675646',
    facebook: 'https://facebook.com',
    instagram: 'https://instagram.com',
    linkedin: 'https://linkedin.com'
  });

  const handleNearbyHotels = (e) => {
    e.preventDefault();
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      navigate('/search');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
          const data = await response.json();
          const city = data.city || data.locality || '';

          if (city) {
            navigate(`/search?city=${encodeURIComponent(city)}`);
          } else {
            navigate('/search');
          }
        } catch (error) {
          console.error("Error fetching city:", error);
          navigate('/search');
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        navigate('/search');
      },
      { timeout: 10000 }
    );
  };

  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8001/api/v1';
        const cleanApiBase = apiBase.replace(/\/+$/, '');
        const response = await fetch(`${cleanApiBase}/contact-details`);
        const result = await response.json();
        if (result.status && result.data) {
          setContactDetails(prev => ({
            ...prev,
            ...result.data
          }));
        }
      } catch (error) {
        console.error('Failed to fetch contact details:', error);
      }
    };
    fetchContactDetails();
  }, []);

  const toggleMobileSection = (sectionName) => {
    setMobileExpandedSection(prev => prev === sectionName ? null : sectionName);
  };

  // Curated city-wise directory data (India Stays Only)
  const cityDirectory = {
    popular: {
      id: 'popular',
      title: 'Popular Domestic Cities',
      subtitle: 'Top Metropolitan & Cultural Cities',
      cities: [
        { name: 'Hotels in Ujjain', query: 'Ujjain' },
        { name: 'Hotels in Mumbai', query: 'Mumbai' },
        { name: 'Hotels in Delhi', query: 'Delhi' },
        { name: 'Hotels in Bangalore', query: 'Bangalore' },
        { name: 'Hotels in Goa', query: 'Goa' },
        { name: 'Hotels in Jaipur', query: 'Jaipur' },
        { name: 'Hotels in Hyderabad', query: 'Hyderabad' },
        { name: 'Hotels in Kolkata', query: 'Kolkata' },
        { name: 'Hotels in Chennai', query: 'Chennai' },
        { name: 'Hotels in Pune', query: 'Pune' },
        { name: 'Hotels in Ahmedabad', query: 'Ahmedabad' },
        { name: 'Hotels in Indore', query: 'Indore' },
        { name: 'Hotels in Bhopal', query: 'Bhopal' },
        { name: 'Hotels in Lucknow', query: 'Lucknow' },
        { name: 'Hotels in Chandigarh', query: 'Chandigarh' },
        { name: 'Hotels in Kochi', query: 'Kochi' },
        { name: 'Hotels in Agra', query: 'Agra' },
        { name: 'Hotels in Surat', query: 'Surat' }
      ]
    },
    pilgrimage: {
      id: 'pilgrimage',
      title: 'Spiritual & Pilgrimage Stays',
      subtitle: 'Sacred Temples & Holy Circuits',
      cities: [
        { name: 'Hotels in Ujjain (Mahakal)', query: 'Ujjain' },
        { name: 'Hotels in Varanasi (Kashi)', query: 'Varanasi' },
        { name: 'Hotels in Ayodhya (Ram Mandir)', query: 'Ayodhya' },
        { name: 'Hotels in Haridwar', query: 'Haridwar' },
        { name: 'Hotels in Rishikesh', query: 'Rishikesh' },
        { name: 'Hotels in Tirupati (Balaji)', query: 'Tirupati' },
        { name: 'Hotels in Shirdi (Sai Baba)', query: 'Shirdi' },
        { name: 'Hotels in Puri (Jagannath)', query: 'Puri' },
        { name: 'Hotels in Madurai', query: 'Madurai' },
        { name: 'Hotels in Amritsar (Golden Temple)', query: 'Amritsar' },
        { name: 'Hotels in Mathura & Vrindavan', query: 'Mathura' },
        { name: 'Hotels in Katra (Vaishno Devi)', query: 'Katra' },
        { name: 'Hotels in Dwarka', query: 'Dwarka' },
        { name: 'Hotels in Somnath', query: 'Somnath' },
        { name: 'Hotels in Bodh Gaya', query: 'Gaya' },
        { name: 'Hotels in Rameshwaram', query: 'Rameshwaram' }
      ]
    },
    hillstations: {
      id: 'hillstations',
      title: 'Hill Stations & Nature Getaways',
      subtitle: 'Scenic Mountain & Valley Retreats',
      cities: [
        { name: 'Hotels in Manali', query: 'Manali' },
        { name: 'Hotels in Shimla', query: 'Shimla' },
        { name: 'Hotels in Ooty', query: 'Ooty' },
        { name: 'Hotels in Munnar', query: 'Munnar' },
        { name: 'Hotels in Udaipur', query: 'Udaipur' },
        { name: 'Hotels in Nainital', query: 'Nainital' },
        { name: 'Hotels in Mussoorie', query: 'Mussoorie' },
        { name: 'Hotels in Darjeeling', query: 'Darjeeling' },
        { name: 'Hotels in Coorg', query: 'Coorg' },
        { name: 'Hotels in Mount Abu', query: 'Mount Abu' },
        { name: 'Hotels in Kodaikanal', query: 'Kodaikanal' },
        { name: 'Hotels in Lonavala', query: 'Lonavala' },
        { name: 'Hotels in Wayanad', query: 'Wayanad' },
        { name: 'Hotels in Gangtok', query: 'Gangtok' },
        { name: 'Hotels in Mahabaleshwar', query: 'Mahabaleshwar' },
        { name: 'Hotels in McLeod Ganj', query: 'Dharamshala' }
      ]
    },
    staytypes: {
      id: 'staytypes',
      title: 'Stay Themes & Categories',
      subtitle: 'Curated Lodging Experiences',
      cities: [
        { name: 'Luxury 5-Star Hotels', query: 'Luxury' },
        { name: 'Budget Friendly Stays', query: 'Budget' },
        { name: 'Heritage Havelis & Palaces', query: 'Heritage' },
        { name: 'Beachfront Resorts & Spas', query: 'Resort' },
        { name: 'Couple Friendly Stays', query: 'Couple Friendly' },
        { name: 'Business & Transit Hotels', query: 'Business' },
        { name: 'Pet Friendly Homestays', query: 'Pet Friendly' },
        { name: 'Airport Hotels', query: 'Airport' },
        { name: 'Private Pool Villas', query: 'Villa' },
        { name: 'Wellness & Ayurveda Retreats', query: 'Wellness' },
        { name: 'Family Suites & Cottages', query: 'Family' },
        { name: 'Boutique City Apartments', query: 'Apartment' }
      ]
    }
  };

  return (
    <footer className="footer-root bg-white border-top text-secondary position-relative">

      {/* ── Desktop & Tablet Enterprise Footer (Hidden on Mobile) ─────── */}
      <div className="d-none d-md-block">

        {/* ── 1. Top Value Proposition & Trust Strip ───────────────────── */}
        <div className="border-bottom bg-light bg-opacity-75 py-4">
          <div className="container">
            <div className="row g-3 g-lg-4 text-center text-md-start">
              <div className="col-6 col-lg-3 d-flex align-items-center gap-3">
                <div className="p-2.5 rounded-circle bg-primary bg-opacity-10 text-primary flex-shrink-0">
                  <Building2 size={24} />
                </div>
                <div>
                  <h6 className="fw-bold text-dark mb-0 fs-6">500,000+ Verified Stays</h6>
                  <small className="text-muted d-none d-sm-block">Top hotels, resorts & villas</small>
                </div>
              </div>

              <div className="col-6 col-lg-3 d-flex align-items-center gap-3">
                <div className="p-2.5 rounded-circle bg-success bg-opacity-10 text-success flex-shrink-0">
                  <Award size={24} />
                </div>
                <div>
                  <h6 className="fw-bold text-dark mb-0 fs-6">Best Price Guarantee</h6>
                  <small className="text-muted d-none d-sm-block">Honest rates with zero hidden fees</small>
                </div>
              </div>

              <div className="col-6 col-lg-3 d-flex align-items-center gap-3">
                <div className="p-2.5 rounded-circle bg-warning bg-opacity-10 text-warning flex-shrink-0">
                  <Sparkles size={24} />
                </div>
                <div>
                  <h6 className="fw-bold text-dark mb-0 fs-6">Instant Confirmation</h6>
                  <small className="text-muted d-none d-sm-block">Real-time SMS & digital vouchers</small>
                </div>
              </div>

              <div className="col-6 col-lg-3 d-flex align-items-center gap-3">
                <div className="p-2.5 rounded-circle bg-info bg-opacity-10 text-info flex-shrink-0">
                  <Clock size={24} />
                </div>
                <div>
                  <h6 className="fw-bold text-dark mb-0 fs-6">24/7 Dedicated Support</h6>
                  <small className="text-muted d-none d-sm-block">Round-the-clock concierge help</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── 2. City-Wise Hotels Directory (All Collections - Unique Showcase) ── */}
        <div className="py-4 py-md-5 border-bottom bg-white">
          <div className="container">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
              <div>
                <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                  <Globe size={18} className="text-primary" />
                  <span>Explore Hotels by Destination & City</span>
                </h5>
                <p className="text-muted small mb-0">Browse top-rated hotels, pilgrimage stays, and holiday getaways across India.</p>
              </div>
            </div>

            {/* All 4 Categories Rendered in a Unique Thematic Showcase */}
            <div className="d-flex flex-column gap-3.5">
              {Object.values(cityDirectory).map((category) => (
                <div key={category.id} className="directory-category-card p-3 p-md-3.5 rounded-4 bg-light bg-opacity-60 border border-light-subtle">
                  
                  {/* Category Header */}
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2.5 pb-2 border-bottom border-light-subtle">
                    <div className="d-flex align-items-center gap-2">
                      <div className="p-1.5 rounded-3 bg-white shadow-xs border border-light-subtle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                        {category.id === 'popular' && <Building2 size={16} className="text-primary" />}
                        {category.id === 'pilgrimage' && <Sparkles size={16} style={{ color: '#ea580c' }} />}
                        {category.id === 'hillstations' && <Compass size={16} style={{ color: '#10b981' }} />}
                        {category.id === 'staytypes' && <Award size={16} style={{ color: '#6366f1' }} />}
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-0 fs-6">{category.title}</h6>
                        <span className="text-muted" style={{ fontSize: '11px' }}>{category.subtitle}</span>
                      </div>
                    </div>

                    <span className="badge rounded-pill bg-white text-secondary border px-2.5 py-1 shadow-xs" style={{ fontSize: '11px', fontWeight: '600' }}>
                      {category.cities.length} Curated Stays
                    </span>
                  </div>

                  {/* Destination Chips / Pills Cloud */}
                  <div className="d-flex flex-wrap gap-2">
                    {category.cities.map((city, idx) => (
                      <Link
                        key={idx}
                        to={`/search?city=${encodeURIComponent(city.query)}`}
                        className="directory-pill-chip text-decoration-none rounded-pill px-3 py-1.5 transition"
                        title={city.name}
                      >
                        <span className="chip-bullet me-1.5 text-primary">›</span>
                        <span className="chip-text">{city.name}</span>
                      </Link>
                    ))}
                  </div>

                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── 3. Multi-Column Corporate Footer Navigation ──────────────────── */}
        <div className="py-5 bg-white">
          <div className="container">
            <div className="row gy-4 align-items-start">

              {/* Column 1: Brand Info */}
              <div className="col-12 col-lg-3 col-md-12 pe-lg-4">
                <div className="footer-col-header mb-3">
                  <Link to="/" className="d-inline-flex align-items-center">
                    <img src={logo} className="img-fluid" style={{ maxHeight: '38px', width: 'auto' }} alt="ZoyoTrip" />
                  </Link>
                </div>
                <p className="text-muted small lh-base mb-3">
                  ZoyoTrip is India's fast-growing travel booking platform delivering unbeatable hotel stays, verified accommodations, luxury resorts, and nationwide travel experiences.
                </p>

                <div className="d-flex align-items-center gap-2 mb-3">
                  <ShieldCheck size={18} className="text-success" />
                  <span className="small text-dark fw-semibold">100% Secure & Trusted Bookings</span>
                </div>

                {/* Social Media Links */}
                <div className="d-flex gap-2">
                  {contactDetails.facebook && (
                    <a href={contactDetails.facebook} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light rounded-circle p-2 text-primary social-btn" aria-label="Facebook">
                      <i className="bi bi-facebook fs-6"></i>
                    </a>
                  )}
                  {contactDetails.instagram && (
                    <a href={contactDetails.instagram} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light rounded-circle p-2 text-danger social-btn" aria-label="Instagram">
                      <i className="bi bi-instagram fs-6"></i>
                    </a>
                  )}
                  {contactDetails.linkedin && (
                    <a href={contactDetails.linkedin} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light rounded-circle p-2 text-primary social-btn" aria-label="LinkedIn">
                      <i className="bi bi-linkedin fs-6"></i>
                    </a>
                  )}
                </div>
              </div>

              {/* Column 2: Explore Stays */}
              <div className="col-6 col-lg-2 col-md-3">
                <h6 className="footer-col-header text-dark fw-bold mb-3 fs-6">
                  Explore Stays
                </h6>
                <ul className="list-unstyled small d-flex flex-column gap-2 mb-0">
                  <li><Link to="/search" className="text-muted text-decoration-none footer-nav-link">Search All Hotels</Link></li>
                  <li><a href="#nearby" onClick={handleNearbyHotels} style={{ cursor: 'pointer' }} className="text-muted text-decoration-none footer-nav-link">Nearby Hotels</a></li>
                  <li><Link to="/offers" className="text-muted text-decoration-none footer-nav-link d-inline-flex align-items-center gap-1"><span>Offers & Coupons</span><span className="badge bg-danger rounded-pill px-1.5 py-0.5" style={{ fontSize: '9px' }}>HOT</span></Link></li>
                  <li><Link to="/search?city=Goa" className="text-muted text-decoration-none footer-nav-link">Top Rated Hotels</Link></li>
                  <li><Link to="/blogs" className="text-muted text-decoration-none footer-nav-link">Travel Blogs & Guides</Link></li>
                  <li><Link to="/faq" className="text-muted text-decoration-none footer-nav-link">Help & FAQs</Link></li>
                </ul>
              </div>

              {/* Column 3: Company */}
              <div className="col-6 col-lg-2 col-md-3">
                <h6 className="footer-col-header text-dark fw-bold mb-3 fs-6">
                  Company
                </h6>
                <ul className="list-unstyled small d-flex flex-column gap-2 mb-0">
                  <li><Link to="/about-us" className="text-muted text-decoration-none footer-nav-link">About ZoyoTrip</Link></li>
                  <li><Link to="/contact-us" className="text-muted text-decoration-none footer-nav-link">Contact Support</Link></li>
                  <li><Link to="/about-us" className="text-muted text-decoration-none footer-nav-link">Careers & Culture</Link></li>
                  <li><Link to="/about-us" className="text-muted text-decoration-none footer-nav-link">Press & Media</Link></li>
                  <li><Link to="/about-us" className="text-muted text-decoration-none footer-nav-link">Corporate Stays</Link></li>
                </ul>
              </div>

              {/* Column 4: Policies & Trust */}
              <div className="col-6 col-lg-2 col-md-3">
                <h6 className="footer-col-header text-dark fw-bold mb-3 fs-6">
                  Policies & Trust
                </h6>
                <ul className="list-unstyled small d-flex flex-column gap-2 mb-0">
                  <li><Link to="/privacy-policy" className="text-muted text-decoration-none footer-nav-link">Privacy Policy</Link></li>
                  <li><Link to="/terms-and-conditions" className="text-muted text-decoration-none footer-nav-link">Terms of Service</Link></li>
                  <li><Link to="/cancellation-policy" className="text-muted text-decoration-none footer-nav-link">Cancellation & Refunds</Link></li>
                  <li><Link to="/terms-and-conditions" className="text-muted text-decoration-none footer-nav-link">Guest Booking Policy</Link></li>
                  <li><Link to="/privacy-policy" className="text-muted text-decoration-none footer-nav-link">Data Security & Safety</Link></li>
                  <li><Link to="/faq" className="text-muted text-decoration-none footer-nav-link">Fraud Advisory</Link></li>
                </ul>
              </div>

              {/* Column 5: Corporate Office */}
              <div className="col-12 col-lg-3 col-md-3">
                <h6 className="footer-col-header text-dark fw-bold mb-3 fs-6">
                  Corporate Office
                </h6>

                <div className="d-flex flex-column gap-2 small text-muted">
                  {contactDetails.address && (
                    <div className="d-flex align-items-start gap-2">
                      <MapPin size={17} className="text-primary mt-0.5 flex-shrink-0" />
                      <span>{contactDetails.address}</span>
                    </div>
                  )}

                  {contactDetails.phone && (
                    <div className="d-flex align-items-center gap-2">
                      <Phone size={16} className="text-success flex-shrink-0" />
                      <a href={`tel:${contactDetails.phone.replace(/[^0-9+]/g, '')}`} className="text-dark fw-bold text-decoration-none footer-contact-link">
                        {contactDetails.phone}
                      </a>
                    </div>
                  )}

                  {contactDetails.email && (
                    <div className="d-flex align-items-center gap-2">
                      <Mail size={16} className="text-primary flex-shrink-0" />
                      <a href={`mailto:${contactDetails.email}`} className="text-muted text-decoration-none footer-contact-link">
                        {contactDetails.email}
                      </a>
                    </div>
                  )}
                </div>

                <div className="p-2.5 rounded-3 bg-light border mt-2.5">
                  <span className="small text-muted d-block mb-0.5" style={{ fontSize: '11.5px' }}>Need instant booking assistance?</span>
                  <span className="small fw-bold text-dark d-flex align-items-center gap-1.5" style={{ fontSize: '12px' }}>
                    <span className="badge bg-success p-1 rounded-circle"></span>
                    Our 24x7 live desk is active now
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ── 4. Payment Partners & Safe Checkout Assurance ──────────────── */}
        <div className="py-3.5 border-top border-bottom bg-light bg-opacity-75">
          <div className="container">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <div className="d-flex align-items-center gap-1.5 text-dark fw-bold small me-2">
                  <ShieldCheck size={18} className="text-success" />
                  <span>100% Safe & Secure Payment Partners:</span>
                </div>

                {/* Payment Partner Badges with Icons */}
                <div className="d-flex align-items-center gap-2 flex-wrap">

                  {/* 1. Razorpay Verified */}
                  <div className="d-inline-flex align-items-center gap-1.5 px-2.5 py-1.5 rounded-3 bg-white border shadow-xs payment-badge" title="Razorpay Verified Gateway">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M4 14.5L13.5 2L11 10.5H19.5L10 23L12.5 14.5H4Z" fill="#0C2340" />
                    </svg>
                    <span className="fw-bold text-dark" style={{ fontSize: '12px' }}>Razorpay</span>
                    <span className="badge bg-success bg-opacity-15 text-success rounded-pill px-1.5 py-0.5 d-inline-flex align-items-center gap-0.5" style={{ fontSize: '10px' }}>
                      <ShieldCheck size={10} className="text-white" />
                      <span className='text-white'>Verified</span>
                    </span>
                  </div>

                  {/* 2. UPI (GPay / PhonePe / Paytm) */}
                  <div className="d-inline-flex align-items-center gap-2 px-2.5 py-1.5 rounded-3 bg-white border shadow-xs payment-badge" title="UPI - GPay, PhonePe, Paytm, BHIM">
                    <svg width="20" height="14" viewBox="0 0 32 20" fill="none">
                      <path d="M10 18L18 2L22 18H10Z" fill="#097939" />
                      <path d="M16 18L22 8L26 18H16Z" fill="#E77817" />
                    </svg>
                    <div className="d-flex flex-column lh-1">
                      <span className="fw-black text-dark" style={{ fontSize: '12px', letterSpacing: '0.3px' }}>UPI</span>
                      <span className="text-muted" style={{ fontSize: '9px', marginTop: '1px' }}>GPay · PhonePe · Paytm</span>
                    </div>
                  </div>

                  {/* 3. Visa & MasterCard */}
                  <div className="d-inline-flex align-items-center gap-2 px-2.5 py-1.5 rounded-3 bg-white border shadow-xs payment-badge" title="Visa & MasterCard">
                    <span className="fw-black text-primary font-monospace" style={{ fontSize: '13px', fontStyle: 'italic', letterSpacing: '-0.5px' }}>VISA</span>
                    <div className="d-inline-flex align-items-center" style={{ marginLeft: '-3px' }}>
                      <svg width="22" height="15" viewBox="0 0 36 24" fill="none">
                        <circle cx="12" cy="12" r="10" fill="#EB001B" />
                        <circle cx="24" cy="12" r="10" fill="#F79E1B" fillOpacity="0.9" />
                        <path d="M18 5.33a9.98 9.98 0 0 0-4.67 6.67c.75 2.67 2.47 4.9 4.67 6.67 2.2-1.77 3.92-4 4.67-6.67A9.98 9.98 0 0 0 18 5.33z" fill="#FF5F00" />
                      </svg>
                    </div>
                    <span className="fw-semibold text-dark" style={{ fontSize: '11px' }}>Mastercard</span>
                  </div>

                  {/* 4. RuPay */}
                  <div className="d-inline-flex align-items-center gap-1.5 px-2.5 py-1.5 rounded-3 bg-white border shadow-xs payment-badge" title="RuPay Debit & Credit Cards">
                    <svg width="22" height="15" viewBox="0 0 36 20" fill="none">
                      <path d="M4 16L11 4H17L10 16H4Z" fill="#0072BC" />
                      <path d="M13 16L20 4H26L19 16H13Z" fill="#F37023" />
                      <path d="M22 16L29 4H35L28 16H22Z" fill="#00A651" />
                    </svg>
                    <span className="fw-black text-dark" style={{ fontSize: '12px' }}>RuPay</span>
                  </div>

                  {/* 5. NetBanking */}
                  <div className="d-inline-flex align-items-center gap-1.5 px-2.5 py-1.5 rounded-3 bg-white border shadow-xs payment-badge" title="NetBanking for 50+ Banks">
                    <Building2 size={16} className="text-primary" />
                    <div className="d-flex flex-column lh-1">
                      <span className="fw-bold text-dark" style={{ fontSize: '12px' }}>NetBanking</span>
                      <span className="text-muted" style={{ fontSize: '9px', marginTop: '1px' }}>50+ Major Banks</span>
                    </div>
                  </div>

                </div>
              </div>

              <div className="d-flex align-items-center gap-2 text-muted small ms-auto ms-lg-0">
                <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2.5 py-1.5 d-inline-flex align-items-center gap-1">
                  <span>🔒</span>
                  <span>256-Bit SSL Encrypted Checkout</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── 5. Copyright & Bottom Bar ──────────────────────────────────── */}
        <div className="py-3 bg-white pb-5 pb-lg-3 mb-4 mb-lg-0">
          <div className="container">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 small text-muted">
              <div>
                &copy; {new Date().getFullYear()} <strong className="text-primary">ZoyoTrip</strong>. All rights reserved.
              </div>
              <div>
                Developed by{' '}
                <a
                  href="https://zapprex.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary fw-bold text-decoration-none"
                >
                  Zapprex Technologies
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ── End of Desktop & Tablet Enterprise Footer ──────────────────── */}

      {/* ── Mobile View: Big "Made with Love in India" Footer ─────────── */}
      <div className="d-block d-md-none bg-white py-4 px-3 text-center border-top">
        {/* Big Text About Love with India */}
        <div className="pt-2 pb-1">
          <h3 className="fw-black text-dark mb-1 d-inline-flex align-items-center justify-content-center gap-2 flex-wrap" style={{ fontSize: '24px', letterSpacing: '-0.4px' }}>
            <span>Made with</span>
            <span className="text-danger animate-pulse" style={{ display: 'inline-block' }}>❤️</span>
            <span>in India</span>
            <span style={{ fontSize: '24px' }}>🇮🇳</span>
          </h3>
        </div>
      </div>

      <style>{`
        .directory-link {
          transition: all 0.2s ease-in-out;
          color: #4b5563 !important;
        }
        .directory-link:hover {
          color: #0d6efd !important;
          transform: translateX(2px);
        }
        .directory-link:hover .bullet {
          color: #0a58ca !important;
        }
        .footer-nav-link {
          transition: color 0.2s ease;
        }
        .footer-nav-link:hover {
          color: #0d6efd !important;
          text-decoration: underline !important;
        }
        .footer-contact-link:hover {
          color: #0d6efd !important;
        }
        .social-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s ease, background-color 0.2s ease;
        }
        .social-btn:hover {
          transform: translateY(-2px);
          background-color: #e9ecef !important;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .payment-badge {
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
          user-select: none;
        }
        .payment-badge:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08) !important;
          border-color: #cbd5e1 !important;
        }
        .shadow-xs {
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
        }
        .footer-col-header {
          height: 38px;
          min-height: 38px;
          display: flex;
          align-items: center;
          margin-top: 0;
          margin-bottom: 1rem;
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.2px;
        }
        .directory-category-card {
          transition: border-color 0.25s ease, box-shadow 0.25s ease;
        }
        .directory-category-card:hover {
          border-color: #cbd5e1 !important;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04) !important;
        }
        .directory-pill-chip {
          background-color: #ffffff;
          color: #475569;
          border: 1px solid #e2e8f0;
          font-size: 13px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        }
        .directory-pill-chip:hover {
          background-color: #0d6efd;
          color: #ffffff !important;
          border-color: #0d6efd;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.2);
        }
        .directory-pill-chip:hover .chip-bullet {
          color: #ffffff !important;
        }
      `}</style>
    </footer>
  );
}

export default Footer;
